import React, { useEffect, useRef, useState } from 'react';
import {
  Datagrid,
  FunctionField,
  SelectInput,
  TextField,
  useRefresh,
} from 'react-admin';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ClearIcon from '@material-ui/icons/Clear';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import CreateIcon from '@material-ui/icons/Create';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DescriptionIcon from '@material-ui/icons/Description';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import EventIcon from '@material-ui/icons/Event';
import FlagIcon from '@material-ui/icons/Flag';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LoopIcon from '@material-ui/icons/Loop';
import NoteAddOutlinedIcon from '@material-ui/icons/NoteAddOutlined';
import PeopleIcon from '@material-ui/icons/People';
import PublishOutlinedIcon from '@material-ui/icons/PublishOutlined';
import TodayIcon from '@material-ui/icons/Today';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import AbasNavegador from '../../Components/AbasNavegador';
import FiltrosTabela from '../../Components/FiltrosTabela';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageTabbedComBuscaPagina from '../../Components/ReactAdmin/List/ListPageTabbedComBuscaPagina';
import TabelaDados, { AcoesLinha } from '../../Components/TabelaDados';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { api, getListAPI } from '../../services';
import { PRIMARY_COLOR } from '../../utils';
import { STATUS_ATIVO, STATUS_INATIVO } from '../../utils/constants';
import { PERMISSAO_TELA_MODELOS_DOCUMENTOS_LISTAR } from '../../utils/dadosTelas';
import {
  BlocoDetalhe,
  Campo,
  corStatus,
  formatarDataHora,
  gradeCampos,
  renderAssinatura,
  renderColaborador,
  renderCompetencia,
  renderCriadoEm,
  renderDocumento,
  renderStatus,
  tipoDoBlob,
  urlDoArquivo,
  useAcoesDocumentos,
  VisualizadorArquivo,
} from '../Documentos/List';
import DialogoCreateEdit, {
  CAMPOS_FILTRO_COLABORADOR,
  TextoDoModelo,
} from './DialogoCreateEdit';
import DialogoGerarDocumentos from './DialogoGerarDocumentos';
import {
  choicesAbrangencia,
  choicesAcao,
  choicesOrigem,
  choicesQuemAssina,
  choicesRecorrencia,
} from './Filter';

const tabs = [
  {
    id: 'ativos',
    name: 'Ativos',
    cor: '#388e3c',
    icon: <CheckCircleOutlineIcon fontSize="small" />,
    filter: { status: STATUS_ATIVO },
  },
  {
    id: 'inativos',
    name: 'Inativos',
    cor: '#757575',
    icon: <ClearIcon fontSize="small" />,
    filter: { status: STATUS_INATIVO },
  },
];

// Mesmos filtros do antigo Filter.js, no formato do drawer "Filtros avançados".
// "Nome" virou a busca rápida do cabeçalho.
const advancedFilters = [
  <SelectInput fullWidth label="Ação" source="acao" choices={choicesAcao} />,
  <SelectInput
    fullWidth
    label="Tipo de documento"
    source="origem"
    choices={choicesOrigem}
  />,
  <SelectInput
    fullWidth
    label="Recorrência"
    source="recorrencia"
    choices={choicesRecorrencia}
  />,
  <SelectInput
    fullWidth
    label="Quem assina"
    source="quem_assina"
    choices={choicesQuemAssina}
  />,
  <SelectInput
    fullWidth
    label="Abrangência"
    source="abrangencia"
    choices={choicesAbrangencia}
  />,
];

const textoAcao = {
  assinar: 'Assinar',
  visualizar: 'Visualizar',
  enviar_arquivo: 'Enviar arquivo',
};

// Mesmas cores da tela Minhas pendências (assinar, ler e enviar arquivo).
const corAcao = {
  assinar: { cor: '#8a4600', fundo: '#fff4e5' },
  visualizar: { cor: '#6a1b9a', fundo: '#f3e5f5' },
  enviar_arquivo: { cor: '#01579b', fundo: '#e5f6fd' },
};

const ChipAcao = ({ acao }) => {
  const { cor, fundo } = corAcao[acao] || { cor: '#424242', fundo: '#f5f5f5' };
  return (
    <Chip
      size="small"
      label={textoAcao[acao] || '—'}
      style={{
        backgroundColor: fundo,
        color: cor,
        border: `1px solid ${cor}33`,
        fontWeight: 600,
      }}
    />
  );
};

const textoRecorrencia = {
  unica: 'Única',
  sob_demanda: 'Sob demanda',
  mensal: 'Mensal',
  anual: 'Anual',
};

const textoQuemAssina = {
  colaborador: 'Colaborador',
  usuario_fixo: 'Pessoa fixa',
};

const textoAbrangencia = {
  um: 'Um',
  varios: 'Vários',
  todos: 'Todos',
};

const textoOrigem = {
  upload: 'Arquivo enviado (PDF ou imagem)',
  texto: 'Texto com variáveis',
  por_colaborador: 'Um arquivo por colaborador',
};

const nomeAssinanteFixo = (u) =>
  u ? (u.colaborador && u.colaborador.nome) || u.username : '—';

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

// Competência no formato do servidor: `AAAA-MM` (mensal) ou `AAAA` (anual).
const competenciaDeHoje = (recorrencia) => {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  if (recorrencia === 'anual') return String(ano);
  return `${ano}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
};

const mudarCompetencia = (competencia, passo) => {
  const [ano, mes] = competencia.split('-').map(Number);
  if (!mes) return String(ano + passo);
  const data = new Date(ano, mes - 1 + passo, 1);
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(
    2,
    '0',
  )}`;
};

const rotuloCompetencia = (competencia) => {
  const [ano, mes] = competencia.split('-');
  return mes ? `${MESES[Number(mes) - 1]} de ${ano}` : ano;
};

// Passador de período: setas, mês/ano clicável (mensal abre um seletor de meses do ano) e atalho
// para voltar ao período atual. Não deixa avançar para o futuro.
const PassadorPeriodo = ({ recorrencia, competencia, onChange }) => {
  const [ancora, setAncora] = useState(null);
  const mensal = recorrencia === 'mensal';
  const atual = competenciaDeHoje(recorrencia);
  const [anoAtual, mesAtual] = atual.split('-').map(Number);
  const [anoSeletor, setAnoSeletor] = useState(Number(competencia.slice(0, 4)));
  const nomePeriodo = mensal ? 'mês' : 'ano';

  function abrirSeletor(evento) {
    setAnoSeletor(Number(competencia.slice(0, 4)));
    setAncora(evento.currentTarget);
  }

  function escolherMes(mes) {
    onChange(`${anoSeletor}-${String(mes).padStart(2, '0')}`);
    setAncora(null);
  }

  return (
    <Box display="flex" alignItems="center" style={{ gap: 8, flexShrink: 0 }}>
      {competencia !== atual && (
        <Button
          size="small"
          color="primary"
          startIcon={<TodayIcon />}
          style={{ textTransform: 'none', fontWeight: 600 }}
          onClick={() => onChange(atual)}
        >
          {mensal ? 'Mês atual' : 'Ano atual'}
        </Button>
      )}
      <Box
        display="flex"
        alignItems="center"
        style={{
          height: 40,
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          backgroundColor: '#fff',
        }}
      >
        <Tooltip title={`Ir para o ${nomePeriodo} anterior`}>
          <IconButton
            size="small"
            onClick={() => onChange(mudarCompetencia(competencia, -1))}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
        {mensal ? (
          <Tooltip title="Escolher o mês">
            <Button
              onClick={abrirSeletor}
              startIcon={<EventIcon />}
              style={{
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 190,
                color: '#0f172a',
              }}
            >
              {rotuloCompetencia(competencia)}
            </Button>
          </Tooltip>
        ) : (
          <Typography
            variant="subtitle2"
            align="center"
            style={{ fontWeight: 700, minWidth: 100 }}
          >
            {rotuloCompetencia(competencia)}
          </Typography>
        )}
        <Tooltip title={`Ir para o ${nomePeriodo} seguinte`}>
          <span>
            <IconButton
              size="small"
              disabled={competencia >= atual}
              onClick={() => onChange(mudarCompetencia(competencia, 1))}
            >
              <ChevronRightIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Popover
        open={Boolean(ancora)}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box p={2} style={{ width: 264 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <IconButton
              size="small"
              onClick={() => setAnoSeletor(anoSeletor - 1)}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              {anoSeletor}
            </Typography>
            <IconButton
              size="small"
              disabled={anoSeletor >= anoAtual}
              onClick={() => setAnoSeletor(anoSeletor + 1)}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Box
            display="grid"
            style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}
          >
            {MESES.map((nome, indice) => {
              const mes = indice + 1;
              const futuro =
                anoSeletor > anoAtual ||
                (anoSeletor === anoAtual && mes > mesAtual);
              const selecionado =
                competencia === `${anoSeletor}-${String(mes).padStart(2, '0')}`;
              return (
                <Button
                  key={nome}
                  size="small"
                  disabled={futuro}
                  variant={selecionado ? 'contained' : 'text'}
                  color={selecionado ? 'primary' : 'default'}
                  style={{ textTransform: 'none', fontWeight: 600 }}
                  onClick={() => escolherMes(mes)}
                >
                  {nome.slice(0, 3)}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

const textoAguardando = {
  assinar: 'Aguardando assinatura',
  enviar_arquivo: 'Aguardando arquivo',
  visualizar: 'Aguardando leitura',
};

// `acoes` vazio = vale para qualquer ação do modelo.
const CARDS_DOCUMENTOS = [
  { titulo: 'Aguardando', status: ['pendente'], acoes: [] },
  { titulo: 'Em análise', status: ['em_analise'], acoes: ['enviar_arquivo'] },
  { titulo: 'Assinados', status: ['assinado'], acoes: ['assinar'] },
  { titulo: 'Enviados', status: ['enviado'], acoes: ['enviar_arquivo'] },
  { titulo: 'Lidos', status: ['lido'], acoes: ['visualizar'] },
  { titulo: 'Só leitura', status: ['emitido'], acoes: ['visualizar'] },
  { titulo: 'Rejeitados', status: ['rejeitado'], acoes: ['enviar_arquivo'] },
  { titulo: 'Cancelados', status: ['cancelado', 'excluido'], acoes: [] },
];

const FILTROS_VAZIOS = {
  colaborador: null,
  funcao: '',
  loja: '',
  sexo: '',
  assinante: null,
  criado_de: '',
  criado_ate: '',
  codinome: null,
  equipe: null,
  grupo: null,
};

// Assinar com arquivo enviado, ou assinar/visualizar com texto de variáveis personalizadas, em recorrência única/mensal/anual: o RH preenche o
// documento de cada colaborador na aba Documentos (não há diálogo Gerar).
const ehPorColaborador = (m) => {
  if (!['unica', 'mensal', 'anual'].includes(m.recorrencia)) return false;
  if (m.origem === 'por_colaborador') return true;
  if (m.origem !== 'texto' || !['assinar', 'visualizar'].includes(m.acao))
    return false;
  try {
    return JSON.parse(m.variaveis_personalizadas || '[]').length > 0;
  } catch (e) {
    return false;
  }
};

// Coluna da tabela -> campo de ordenação do servidor.
const COLUNA_ORDENACAO = {
  colaborador: 'colaborador',
  competencia: 'competencia',
  criado_em: 'created_at',
  status: 'status',
};

// Assinar/visualizar com arquivo enviado ou texto (sem variáveis personalizadas), em recorrência
// única/mensal/anual: o cron gera sozinho (a única, só com abrangência salva), então não há
// diálogo Gerar; o detalhe só mostra quem ainda não tem documento.
const ehGeradoPeloCron = (m) =>
  ['assinar', 'visualizar'].includes(m.acao) &&
  ['upload', 'texto'].includes(m.origem) &&
  (['mensal', 'anual'].includes(m.recorrencia) ||
    (m.recorrencia === 'unica' && Boolean(m.abrangencia))) &&
  !ehPorColaborador(m);

const AbaDocumentosModelo = ({ modelo }) => {
  const { enqueueSnackbar } = useSnackbar();
  const recorrente = ['mensal', 'anual'].includes(modelo.recorrencia);
  // Por colaborador: a tabela também lista quem ainda não tem documento no período, com a ação
  // de adicionar o arquivo ou, no texto, gerar o documento (cria o documento).
  const geradoPeloCron = ehGeradoPeloCron(modelo);
  const porColaborador = ehPorColaborador(modelo) || geradoPeloCron;
  const porTexto = modelo.origem === 'texto';
  const [alvoGerar, setAlvoGerar] = useState(null);
  const refArquivo = useRef(null);
  const alvoArquivo = useRef(null);
  const [semDocumento, setSemDocumento] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [competencia, setCompetencia] = useState(
    recorrente ? competenciaDeHoje(modelo.recorrencia) : null,
  );
  const [porStatus, setPorStatus] = useState(null);
  // card selecionado filtra a listagem; null = todos os documentos do período.
  const [cardSelecionado, setCardSelecionado] = useState(null);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [pagina, setPagina] = useState(0);
  // Padrão: por colaborador, crescente.
  const [ordenacao, setOrdenacao] = useState({
    chave: 'colaborador',
    direcao: 'asc',
  });
  const [porPagina, setPorPagina] = useState(25);
  const [lista, setLista] = useState(null);
  const [recarga, setRecarga] = useState(0);
  const [funcoes, setFuncoes] = useState([]);
  const [lojas, setLojas] = useState([]);
  const { acoes, dialogos } = useAcoesDocumentos(() =>
    setRecarga((atual) => atual + 1),
  );

  function escolherCompetencia(nova) {
    setCompetencia(nova);
    setPagina(0);
  }

  function ordenar(chave) {
    setOrdenacao((atual) => ({
      chave,
      direcao:
        atual.chave === chave && atual.direcao === 'asc' ? 'desc' : 'asc',
    }));
    setPagina(0);
  }

  function selecionarCard(indice) {
    setCardSelecionado(cardSelecionado === indice ? null : indice);
    setPagina(0);
  }

  async function buscarColaboradores(texto) {
    const { data } = await getListAPI(
      'colaboradores',
      ['nome', 'ASC'],
      [1, 30],
      { status: STATUS_ATIVO, nome: texto },
      [],
    );
    return data;
  }

  async function buscarEquipes(texto) {
    const { data } = await getListAPI(
      'equipes_colaborador',
      ['nome', 'ASC'],
      [1, 30],
      { nome: texto },
      [],
    );
    return data;
  }

  async function buscarAssinantes(texto) {
    const { data } = await getListAPI(
      'colaboradores',
      ['nome', 'ASC'],
      [1, 30],
      { status: STATUS_ATIVO, is_user: true, nome: texto },
      [],
    );
    return data;
  }

  // Opções dos filtros de função e loja.
  useEffect(() => {
    getListAPI(
      'funcoes_colaboradores',
      ['nome', 'ASC'],
      [1, 100],
      { status: STATUS_ATIVO },
      [],
    )
      .then(({ data }) => setFuncoes(data))
      .catch(() => setFuncoes([]));
    getListAPI('empresa-loja', ['nome_fantasia', 'ASC'], [1, 100], {}, [])
      .then(({ data }) => setLojas(data))
      .catch(() => setLojas([]));
  }, []);

  useEffect(() => {
    setPorStatus(null);
    api
      .get(`/modelos_documentos/${modelo.id}/documentos`, {
        params: competencia ? { competencia } : {},
      })
      .then((response) => setPorStatus(response.data.data.por_status))
      .catch(() => {
        setPorStatus({});
        enqueueSnackbar('Erro ao carregar os documentos do modelo.', {
          variant: 'error',
        });
      });
  }, [modelo.id, competencia, recarga]);

  const contagens = porColaborador
    ? { ...(porStatus || {}), sem_documento: (semDocumento || []).length }
    : porStatus;
  const soma = (status) =>
    status.reduce((total, item) => total + ((contagens || {})[item] || 0), 0);
  const cardsDocumentos = porColaborador
    ? [
        ...CARDS_DOCUMENTOS,
        { titulo: 'Sem documento', status: ['sem_documento'], acoes: [] },
      ]
    : CARDS_DOCUMENTOS;
  const cards = cardsDocumentos
    .map((card, indice) => ({
      ...card,
      indice,
    }))
    .filter((card) => !card.acoes.length || card.acoes.includes(modelo.acao));
  const total = soma(Object.keys(contagens || {}));
  const nomeCard = (card) =>
    card.status[0] === 'pendente' ? textoAguardando[modelo.acao] : card.titulo;
  const selecionado =
    cardSelecionado === null ? null : cardsDocumentos[cardSelecionado];
  const soSemDocumento = Boolean(
    selecionado && selecionado.status[0] === 'sem_documento',
  );
  const totalFiltrado = selecionado ? soma(selecionado.status) : total;
  const filtroDocumentos = { modelo_documento_id: modelo.id };
  if (competencia) filtroDocumentos.competencia = competencia;
  if (selecionado) filtroDocumentos.status = selecionado.status;
  if (filtros.colaborador)
    filtroDocumentos.colaborador_id = filtros.colaborador.id;
  if (filtros.funcao) filtroDocumentos.funcao_id = filtros.funcao;
  if (filtros.loja) filtroDocumentos.loja_id = filtros.loja;
  if (filtros.sexo) filtroDocumentos.sexo = filtros.sexo;
  if (filtros.assinante)
    filtroDocumentos.colaborador_assinante_id = filtros.assinante.id;
  if (filtros.criado_de) filtroDocumentos.criado_de = filtros.criado_de;
  if (filtros.criado_ate) filtroDocumentos.criado_ate = filtros.criado_ate;
  if (filtros.codinome) filtroDocumentos.codinome_id = filtros.codinome.id;
  if (filtros.equipe) filtroDocumentos.equipe_id = filtros.equipe.id;
  if (filtros.grupo) filtroDocumentos.grupo_id = filtros.grupo.id;
  // Filtros que só existem em documentos já gerados: quem está sem documento não entra.
  const filtraSoDocumentos = Boolean(
    filtros.assinante || filtros.criado_de || filtros.criado_ate,
  );
  const chaveFiltro = JSON.stringify(filtroDocumentos);
  const temFiltro =
    filtraSoDocumentos ||
    Boolean(
      filtros.colaborador ||
        filtros.funcao ||
        filtros.loja ||
        filtros.sexo ||
        filtros.codinome ||
        filtros.equipe ||
        filtros.grupo,
    );
  const camposFiltro = [
    {
      chave: 'colaborador',
      rotulo: 'Colaborador',
      tipo: 'busca',
      buscar: buscarColaboradores,
    },
    { chave: 'funcao', rotulo: 'Função', tipo: 'selecao', opcoes: funcoes },
    {
      chave: 'loja',
      rotulo: 'Loja',
      tipo: 'selecao',
      opcoes: lojas,
      rotuloOpcao: 'nome_fantasia',
    },
    {
      chave: 'sexo',
      rotulo: 'Sexo',
      tipo: 'selecao',
      opcoes: [
        { id: 'masculino', nome: 'Masculino' },
        { id: 'feminino', nome: 'Feminino' },
      ],
    },
    ...(modelo.acao === 'assinar'
      ? [
          {
            chave: 'assinante',
            rotulo: 'Assinante',
            tipo: 'busca',
            buscar: buscarAssinantes,
          },
        ]
      : []),
    {
      chave: 'criado_de',
      rotulo: 'Criado a partir de',
      tipo: 'data',
      naoDepoisDe: 'criado_ate',
    },
    {
      chave: 'criado_ate',
      rotulo: 'Criado até',
      tipo: 'data',
      naoAntesDe: 'criado_de',
    },
    {
      chave: 'equipe',
      rotulo: 'Equipe',
      tipo: 'busca',
      buscar: buscarEquipes,
    },
    CAMPOS_FILTRO_COLABORADOR.find((campo) => campo.chave === 'grupo'),
    CAMPOS_FILTRO_COLABORADOR.find((campo) => campo.chave === 'codinome'),
  ];

  // Quem ainda não tem documento no período (só no tipo "um arquivo por colaborador").
  const parametrosSemDocumento = {
    ...(competencia ? { competencia } : {}),
    ...(filtros.colaborador ? { colaborador_id: filtros.colaborador.id } : {}),
    ...(filtros.funcao ? { funcao_id: filtros.funcao } : {}),
    ...(filtros.loja ? { loja_id: filtros.loja } : {}),
    ...(filtros.sexo ? { sexo: filtros.sexo } : {}),
    ...(filtros.codinome ? { codinome_id: filtros.codinome.id } : {}),
    ...(filtros.equipe ? { equipe_id: filtros.equipe.id } : {}),
    ...(filtros.grupo ? { grupo_id: filtros.grupo.id } : {}),
  };
  const chaveSemDocumento = JSON.stringify(parametrosSemDocumento);
  useEffect(() => {
    if (!porColaborador) return;
    if (filtraSoDocumentos) {
      setSemDocumento([]);
      return;
    }
    setSemDocumento(null);
    api
      .get(`/modelos_documentos/${modelo.id}/sem-documento`, {
        params: parametrosSemDocumento,
      })
      .then((response) => setSemDocumento(response.data.data.data))
      .catch(() => {
        setSemDocumento([]);
        enqueueSnackbar('Erro ao carregar os colaboradores sem documento.', {
          variant: 'error',
        });
      });
  }, [modelo.id, chaveSemDocumento, filtraSoDocumentos, recarga]);

  // Escolha do arquivo de um colaborador sem documento: cria o documento do período.
  function adicionarArquivo(registro) {
    alvoArquivo.current = registro;
    refArquivo.current.click();
  }

  async function arquivoEscolhido(event) {
    const [arquivo] = event.target.files;
    // Permite escolher de novo o mesmo arquivo depois de uma recusa.
    event.target.value = '';
    const alvo = alvoArquivo.current;
    if (!arquivo || !alvo) return;
    if (
      !['application/pdf', 'image/jpeg', 'image/png'].includes(arquivo.type) ||
      arquivo.size > 10 * 1024 * 1024
    ) {
      enqueueSnackbar('Selecione um PDF ou uma imagem JPG/PNG de até 10 MB.', {
        variant: 'error',
      });
      return;
    }
    setEnviando(true);
    try {
      const formData = new FormData();
      formData.append('colaborador_id', alvo.colaborador_id);
      if (competencia) formData.append('competencia', competencia);
      formData.append('file', arquivo);
      await api.post(
        `/modelos_documentos/${modelo.id}/documento-arquivo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      enqueueSnackbar(`Documento de ${alvo.colaborador.nome} criado.`, {
        variant: 'success',
      });
      setRecarga((atual) => atual + 1);
    } catch (e) {
      enqueueSnackbar(
        (e.response && e.response.data && e.response.data.message) ||
          'Erro ao criar o documento.',
        { variant: 'error' },
      );
    } finally {
      setEnviando(false);
    }
  }

  // Um arquivo por colaborador: traz o período todo e pagina aqui (junto de quem não tem documento).
  const paginaDoServidor = porColaborador ? 0 : pagina;
  const porPaginaDoServidor = porColaborador ? 1000 : porPagina;
  useEffect(() => {
    if (soSemDocumento) {
      setLista({ data: [], total: 0 });
      return;
    }
    setLista(null);
    getListAPI(
      'documentos',
      [COLUNA_ORDENACAO[ordenacao.chave], ordenacao.direcao],
      [paginaDoServidor + 1, porPaginaDoServidor],
      filtroDocumentos,
      [],
    )
      .then(setLista)
      .catch(() => {
        setLista({ data: [], total: 0 });
        enqueueSnackbar('Erro ao carregar a listagem de documentos.', {
          variant: 'error',
        });
      });
  }, [chaveFiltro, paginaDoServidor, porPaginaDoServidor, recarga, ordenacao]);

  // Linhas de quem ainda não tem documento no período: só "Adicionar arquivo" vale nelas.
  const linhasSemDocumento = (semDocumento || []).map((colaborador) => ({
    id: `sem-${colaborador.id}`,
    sem_documento: true,
    status: 'sem_documento',
    competencia,
    colaborador_id: colaborador.id,
    colaborador: { id: colaborador.id, nome: colaborador.nome },
    modelo: { nome: modelo.nome, acao: modelo.acao, origem: modelo.origem },
  }));
  const acoesDaTabela = porColaborador
    ? [
        // Gerado pelo cron: a linha "Sem documento" é só conferência, sem ação.
        ...(geradoPeloCron
          ? []
          : [
              {
                title: porTexto ? 'Gerar documento' : 'Adicionar arquivo',
                icon: porTexto ? (
                  <NoteAddOutlinedIcon />
                ) : (
                  <CloudUploadOutlinedIcon />
                ),
                disabled: (r) => !r.sem_documento || enviando,
                onClick: porTexto ? (r) => setAlvoGerar(r) : adicionarArquivo,
                propsButton: { color: 'primary' },
              },
            ]),
        ...acoes.map((acao) => ({
          ...acao,
          disabled: (r) =>
            Boolean(r.sem_documento) ||
            (typeof acao.disabled === 'function'
              ? acao.disabled(r)
              : Boolean(acao.disabled)),
        })),
      ]
    : acoes;
  const documentosDaLista = lista ? lista.data : [];
  let linhasDaTabela = documentosDaLista;
  if (porColaborador) {
    if (soSemDocumento) linhasDaTabela = linhasSemDocumento;
    else if (selecionado) linhasDaTabela = documentosDaLista;
    else linhasDaTabela = [...documentosDaLista, ...linhasSemDocumento];
    // Já traz o período todo: ordena aqui (junto de quem não tem documento).
    const sentido = ordenacao.direcao === 'asc' ? 1 : -1;
    const valorOrdem = (registro) =>
      ({
        colaborador: registro.colaborador ? registro.colaborador.nome : '',
        competencia: registro.competencia || '',
        criado_em: registro.created_at || '',
        status: registro.status || '',
      }[ordenacao.chave]);
    linhasDaTabela = [...linhasDaTabela].sort(
      (a, b) =>
        sentido * String(valorOrdem(a)).localeCompare(String(valorOrdem(b))),
    );
  }
  const carregandoTabela =
    lista === null || (porColaborador && semDocumento === null);

  const colunas = [
    {
      chave: 'colaborador',
      titulo: 'Colaborador',
      largura: 240,
      ordenavel: true,
      fixar: 'esquerda',
      render: renderColaborador,
    },
    {
      chave: 'documento',
      titulo: 'Documento',
      largura: 220,
      render: renderDocumento,
    },
    {
      chave: 'competencia',
      titulo: 'Competência',
      largura: 130,
      ordenavel: true,
      render: renderCompetencia,
    },
    {
      chave: 'assinatura',
      titulo: 'Assinatura',
      largura: 240,
      render: renderAssinatura,
    },
    {
      chave: 'criado_em',
      titulo: 'Criado em',
      largura: 160,
      ordenavel: true,
      render: (registro) =>
        registro.sem_documento ? '—' : renderCriadoEm(registro),
    },
    {
      chave: 'status',
      titulo: 'Status',
      largura: 170,
      ordenavel: true,
      render: renderStatus,
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      largura: porColaborador ? 230 : 190,
      fixar: 'direita',
      render: (registro) => (
        <AcoesLinha
          acoes={acoesDaTabela}
          registro={registro}
          maxVisiveis={porColaborador ? 5 : 4}
        />
      ),
    },
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        style={{ gap: 16 }}
      >
        {porStatus === null ? (
          <Typography variant="body2">Carregando...</Typography>
        ) : (
          <>
            <Box
              display="flex"
              flexWrap="wrap"
              style={{ gap: 8, flex: '1 1 auto', minWidth: 0 }}
            >
              {[
                {
                  indice: null,
                  titulo: 'Todos',
                  status: [],
                  cor: PRIMARY_COLOR,
                },
                ...cards.map((card) => ({
                  ...card,
                  titulo: nomeCard(card),
                  cor: corStatus[card.status[0]],
                })),
              ].map((card) => {
                const ativo = cardSelecionado === card.indice;
                return (
                  <Paper
                    key={card.titulo}
                    variant="outlined"
                    onClick={() => selecionarCard(card.indice)}
                    style={{
                      position: 'relative',
                      padding: '4px 12px',
                      minWidth: 96,
                      cursor: 'pointer',
                      // Card colorido: fundo e borda na cor do status; o selecionado fica mais forte.
                      border: ativo
                        ? `2px solid ${card.cor}`
                        : `1px solid ${card.cor}66`,
                      borderLeft: `4px solid ${card.cor}`,
                      backgroundColor: ativo
                        ? `${card.cor}38`
                        : `${card.cor}1a`,
                      opacity: cardSelecionado === null || ativo ? 1 : 0.7,
                    }}
                  >
                    {ativo && (
                      <CheckCircleOutlineIcon
                        fontSize="small"
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          fontSize: 16,
                          color: card.cor,
                        }}
                      />
                    )}
                    <Typography
                      variant="subtitle1"
                      style={{
                        fontWeight: 700,
                        lineHeight: 1.3,
                        color: card.cor,
                      }}
                    >
                      {card.indice === null ? total : soma(card.status)}
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{
                        fontWeight: ativo ? 700 : 600,
                        color: ativo ? card.cor : '#475569',
                      }}
                    >
                      {card.titulo}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          </>
        )}
        {recorrente && (
          <PassadorPeriodo
            recorrencia={modelo.recorrencia}
            competencia={competencia}
            onChange={escolherCompetencia}
          />
        )}
      </Box>
      <Box mt={2}>
        <FiltrosTabela
          campos={camposFiltro}
          valores={filtros}
          onChange={(novos) => {
            setFiltros(novos);
            setPagina(0);
          }}
        />
      </Box>
      <Box mt={2}>
        <TabelaDados
          colunas={colunas}
          ordenacao={ordenacao}
          onOrdenar={ordenar}
          linhas={
            porColaborador
              ? linhasDaTabela.slice(
                  pagina * porPagina,
                  (pagina + 1) * porPagina,
                )
              : linhasDaTabela
          }
          total={
            porColaborador ? linhasDaTabela.length : lista ? lista.total : 0
          }
          carregando={carregandoTabela}
          pagina={pagina}
          porPagina={porPagina}
          onMudarPagina={setPagina}
          onMudarPorPagina={(quantidade) => {
            setPorPagina(quantidade);
            setPagina(0);
          }}
          rotuloItens="documentos"
          vazio={
            <>
              <DescriptionIcon style={{ fontSize: 40, color: '#b0bec5' }} />
              <Typography variant="body2" color="textSecondary">
                {selecionado || temFiltro
                  ? 'Nenhum documento neste filtro.'
                  : 'Nenhum documento gerado neste modelo.'}
              </Typography>
            </>
          }
        />
      </Box>
      {ehPorColaborador(modelo) && !porTexto && (
        <input
          ref={refArquivo}
          type="file"
          hidden
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={arquivoEscolhido}
        />
      )}
      {porTexto && (
        <DialogoGerarDocumentos
          open={Boolean(alvoGerar)}
          modelo={modelo}
          colaboradorFixo={alvoGerar ? alvoGerar.colaborador : undefined}
          competencia={competencia}
          onClose={() => {
            setAlvoGerar(null);
            setRecarga((atual) => atual + 1);
          }}
        />
      )}
      {dialogos}
    </>
  );
};

// O que acontece em cada recorrência, conforme a ação e o tipo de documento do modelo.
const explicacaoRecorrencia = (m) => {
  const preenche =
    m.origem === 'texto' ? 'preenche as variáveis do texto' : 'envia o arquivo';
  const periodo = { mensal: 'mês', anual: 'ano' }[m.recorrencia];
  if (m.recorrencia === 'sob_demanda') {
    if (m.acao === 'enviar_arquivo')
      return 'O colaborador envia o arquivo quando precisar (em Minhas pendências) ou o RH gera a pendência para ele. Pode repetir quantas vezes for preciso.';
    if (m.origem === 'por_colaborador')
      return 'O RH gera quando necessário: escolhe um colaborador por vez e envia o arquivo dele (obrigatório).';
    if (ehPorColaborador({ ...m, recorrencia: 'unica' }))
      return 'O RH gera quando necessário, um colaborador por vez, preenchendo as variáveis. Pode repetir quantas vezes for preciso.';
    return 'O RH gera quando necessário, para um, vários ou todos os colaboradores. Pode repetir quantas vezes for preciso.';
  }
  if (m.recorrencia === 'unica') {
    if (m.acao === 'enviar_arquivo' && !m.abrangencia)
      return 'O sistema cria uma pendência de envio para cada colaborador que ainda não enviou.';
    if (m.acao === 'enviar_arquivo')
      return 'O sistema cria uma pendência de envio para cada colaborador da abrangência que ainda não enviou, inclusive para quem entrar depois.';
    if (ehPorColaborador(m))
      return `O RH ${preenche} de cada colaborador da abrangência, um de cada vez, na aba Documentos.`;
    if (m.abrangencia)
      return 'O sistema gera o documento, uma só vez, para cada colaborador da abrangência, inclusive para quem entrar depois.';
    return 'Os destinatários são escolhidos ao gerar; cada colaborador recebe um só.';
  }
  if (m.acao === 'enviar_arquivo')
    return `A cada ${periodo} o sistema cria a pendência de envio para os colaboradores da abrangência.`;
  if (ehPorColaborador(m))
    return `A cada ${periodo} o RH ${preenche} de cada colaborador na aba Documentos (o passador de ${periodo} mostra quem ainda não tem).`;
  return `O sistema gera sozinho um documento por colaborador a cada ${periodo}, a partir do ${periodo} seguinte ao da criação do modelo.`;
};

const DialogoDetalhesModelo = ({ modelo, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfTipo, setPdfTipo] = useState('application/pdf');
  const [aba, setAba] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  // Mantém o último modelo no conteúdo durante a animação de fechamento.
  const m = modelo || {};
  const ativo = m.status === STATUS_ATIVO;

  function fecharPdf() {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  }

  function fechar() {
    fecharPdf();
    setAba(0);
    onClose();
  }

  async function visualizarPdf() {
    try {
      const response = await api.get(`/modelos_documentos/${m.id}/pdf`, {
        responseType: 'blob',
      });
      setPdfTipo(tipoDoBlob(response.data));
      setPdfUrl(urlDoArquivo(response.data));
    } catch (e) {
      enqueueSnackbar('Erro ao abrir o arquivo do modelo.', {
        variant: 'error',
      });
    }
  }

  return (
    <>
      <Dialog open={Boolean(modelo)} onClose={fechar} fullScreen>
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          style={{ gap: 12, backgroundColor: PRIMARY_COLOR, color: 'white' }}
        >
          <div style={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap style={{ fontWeight: 600 }}>
              {m.nome || 'Modelo de documento'}
            </Typography>
            <Typography variant="caption" display="block">
              Modelo de documento
            </Typography>
          </div>
          <Box flex={1} />
          <IconButton size="small" onClick={fechar} style={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <AbasNavegador
          valor={aba}
          onChange={setAba}
          abas={[
            {
              id: 0,
              name: 'Detalhes',
              cor: '#1976d2',
              icon: <InfoOutlinedIcon fontSize="small" />,
            },
            {
              id: 1,
              name: 'Documentos',
              cor: '#388e3c',
              icon: <AssignmentIcon fontSize="small" />,
            },
          ]}
        />
        {aba === 1 && modelo && (
          // Sem padding no topo: a barra e o cabeçalho da tabela ficam presos na borda da área que
          // rola, e um padding ali deixaria uma faixa por onde as linhas aparecem ao rolar.
          <DialogContent style={{ padding: '0 24px 24px' }}>
            <Box pt={3}>
              <AbaDocumentosModelo modelo={modelo} />
            </Box>
          </DialogContent>
        )}
        <DialogContent hidden={aba !== 0} style={{ padding: 24 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
              gap: 16,
              alignItems: 'start',
              marginTop: -16,
            }}
          >
            <BlocoDetalhe titulo="Modelo">
              <Box style={gradeCampos}>
                <Campo
                  icone={<DescriptionIcon fontSize="small" />}
                  rotulo="Nome"
                >
                  {m.nome}
                </Campo>
                <Campo
                  icone={<AssignmentIcon fontSize="small" />}
                  rotulo="Ação"
                >
                  <ChipAcao acao={m.acao} />
                </Campo>
                <Campo icone={<FlagIcon fontSize="small" />} rotulo="Situação">
                  <Chip
                    size="small"
                    label={ativo ? 'Ativo' : 'Inativo'}
                    style={{
                      backgroundColor: ativo ? '#e8f5e9' : '#eeeeee',
                      color: ativo ? '#1b5e20' : '#616161',
                      fontWeight: 600,
                    }}
                  />
                </Campo>
                <Campo
                  icone={<EventIcon fontSize="small" />}
                  rotulo="Criado em"
                >
                  {m.created_at ? formatarDataHora(m.created_at) : '—'}
                </Campo>
              </Box>
              {m.descricao && (
                <Box mt={2}>
                  <Divider />
                  <Box mt={2}>
                    <Campo
                      icone={<InfoOutlinedIcon fontSize="small" />}
                      rotulo="Instruções ao colaborador"
                    >
                      <span style={{ whiteSpace: 'pre-wrap' }}>
                        {m.descricao}
                      </span>
                    </Campo>
                  </Box>
                </Box>
              )}
            </BlocoDetalhe>

            {m.acao !== 'enviar_arquivo' && (
              <BlocoDetalhe titulo="Conteúdo">
                <Campo
                  icone={<DescriptionIcon fontSize="small" />}
                  rotulo="Tipo de documento"
                >
                  {textoOrigem[m.origem]}
                </Campo>
                {m.origem === 'upload' && m.arquivo_referencia && (
                  <Box mt={2}>
                    <Button
                      color="primary"
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={visualizarPdf}
                    >
                      Visualizar arquivo do modelo
                    </Button>
                  </Box>
                )}
                {m.origem === 'upload' && !m.arquivo_referencia && (
                  <Box mt={1}>
                    <Typography variant="caption" color="error">
                      Este modelo ainda não tem arquivo enviado.
                    </Typography>
                  </Box>
                )}
                {m.origem === 'texto' && m.conteudo_texto && (
                  <TextoDoModelo modelo={m} />
                )}
              </BlocoDetalhe>
            )}

            <div>
              {m.acao === 'assinar' && (
                <BlocoDetalhe titulo="Assinatura">
                  <Box style={gradeCampos}>
                    <Campo
                      icone={<CreateIcon fontSize="small" />}
                      rotulo="Quem assina"
                    >
                      {m.mais_assinaturas
                        ? [
                            '1. O próprio colaborador',
                            ...(m.assinantesAdicionais || []).map(
                              (usuario, indice) =>
                                `${indice + 2}. ${nomeAssinanteFixo(usuario)}`,
                            ),
                          ].join(' · ')
                        : null}
                      {!m.mais_assinaturas && m.quem_assina === 'usuario_fixo'
                        ? `Pessoa fixa: ${nomeAssinanteFixo(m.usuarioFixo)}`
                        : null}
                      {!m.mais_assinaturas && m.quem_assina !== 'usuario_fixo'
                        ? 'O próprio colaborador'
                        : null}
                    </Campo>
                  </Box>
                </BlocoDetalhe>
              )}

              <BlocoDetalhe titulo="Geração">
                <Box style={gradeCampos}>
                  <Campo
                    icone={<LoopIcon fontSize="small" />}
                    rotulo="Recorrência"
                  >
                    {textoRecorrencia[m.recorrencia]}
                  </Campo>
                  {Boolean(m.abrangencia) && (
                    <Campo
                      icone={<PeopleIcon fontSize="small" />}
                      rotulo="Abrangência"
                    >
                      {{
                        um: 'Um colaborador',
                        varios: 'Vários colaboradores',
                        todos: 'Todos os colaboradores',
                      }[m.abrangencia] || '—'}
                    </Campo>
                  )}
                </Box>
                {Boolean(m.abrangencia) &&
                  m.abrangencia !== 'todos' &&
                  m.colaboradores &&
                  m.colaboradores.length > 0 && (
                    <Box
                      mt={2}
                      display="flex"
                      flexWrap="wrap"
                      style={{ gap: 6 }}
                    >
                      {m.colaboradores.map((c) => (
                        <Chip key={c.id} size="small" label={c.nome} />
                      ))}
                    </Box>
                  )}
                <Box mt={1}>
                  <Typography variant="caption" color="textSecondary">
                    {explicacaoRecorrencia(m)}
                  </Typography>
                </Box>
              </BlocoDetalhe>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(pdfUrl)} onClose={fecharPdf} fullScreen>
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          style={{ gap: 12, backgroundColor: PRIMARY_COLOR, color: 'white' }}
        >
          <div style={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap style={{ fontWeight: 600 }}>
              {m.nome}
            </Typography>
            <Typography variant="caption" display="block">
              Arquivo do modelo
            </Typography>
          </div>
          <Box flex={1} />
          <IconButton
            size="small"
            onClick={fecharPdf}
            style={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent style={{ padding: 0, display: 'flex' }}>
          {pdfUrl && (
            <VisualizadorArquivo
              url={pdfUrl}
              tipo={pdfTipo}
              titulo="Arquivo do modelo"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const List = (props) => {
  const refDialogoConfirmacao = useRef(null);
  const refInputPdf = useRef(null);
  const novoPdf = useRef({ modelo: null, arquivo: null });
  const refresh = useRefresh();
  const { enqueueSnackbar } = useSnackbar();
  const [dialogo, setDialogo] = useState({
    open: false,
    id: null,
  });

  const [modeloGerar, setModeloGerar] = useState(null);
  const [modeloDetalhes, setModeloDetalhes] = useState(null);
  const [totais, setTotais] = useState({});

  // Total de cada aba (sem os demais filtros), mostrado na bolinha da aba.
  async function carregarTotais() {
    try {
      const resultados = await Promise.all(
        tabs.map((tab) =>
          getListAPI(
            'modelos_documentos',
            ['id', 'desc'],
            [1, 1],
            tab.filter,
            [],
          ),
        ),
      );
      setTotais(
        Object.fromEntries(
          tabs.map((tab, indice) => [tab.id, resultados[indice].total]),
        ),
      );
    } catch (e) {
      setTotais({});
    }
  }

  useEffect(() => {
    carregarTotais();
  }, []);

  function handleCriar() {
    setDialogo({ open: true, id: null });
  }

  function handleEdit({ id, nome, total_pendentes: totalPendentes }) {
    if (Number(totalPendentes) > 0) {
      enqueueSnackbar(
        `O modelo "${nome}" tem ${totalPendentes} ${
          Number(totalPendentes) === 1
            ? 'documento pendente'
            : 'documentos pendentes'
        } e por isso não pode ser editado. Aguarde a conclusão ou cancele os pendentes.`,
        { variant: 'warning' },
      );
      return;
    }
    setDialogo({ open: true, id });
  }

  const handleClose = () => {
    refresh();
    carregarTotais();
  };

  // Chama a rota de status, avisa o resultado e atualiza a lista.
  async function alterarStatus(id, rota, sucesso, falha, confirmar) {
    try {
      await api.put(`/modelos_documentos/status/${rota}/${id}`, { confirmar });
      enqueueSnackbar(sucesso, { variant: 'success' });
    } catch (e) {
      const mensagem =
        e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : falha;
      enqueueSnackbar(mensagem, { variant: 'error' });
    }
    handleClose();
  }

  function handleSubstituirPdf(modelo) {
    novoPdf.current = { modelo, arquivo: null };
    refInputPdf.current.click();
  }

  function pdfEscolhido(event) {
    const [arquivo] = event.target.files;
    // Permite escolher de novo o mesmo arquivo depois de uma recusa.
    event.target.value = '';
    if (!arquivo) return;
    if (
      !['application/pdf', 'image/jpeg', 'image/png'].includes(arquivo.type) ||
      arquivo.size > 10 * 1024 * 1024
    ) {
      enqueueSnackbar('Selecione um PDF ou uma imagem JPG/PNG de até 10 MB.', {
        variant: 'error',
      });
      return;
    }
    novoPdf.current.arquivo = arquivo;
    refDialogoConfirmacao.current.handleOpen(
      'SUBSTITUIR ARQUIVO DO MODELO',
      `O modelo "${novoPdf.current.modelo.nome}" possui documentos pendentes. O novo arquivo vai substituir o anterior neles; quem já visualizou o antigo precisará visualizar de novo. Deseja continuar?`,
      { id: novoPdf.current.modelo.id, codigo: 4 },
    );
  }

  async function substituirPdf(id) {
    try {
      const formData = new FormData();
      formData.append('file', novoPdf.current.arquivo);
      const response = await api.post(
        `/modelos_documentos/${id}/pdf?substituir_pendentes=1`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      enqueueSnackbar(response.data.message, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(
        e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erro ao substituir o arquivo.',
        { variant: 'error' },
      );
    }
    handleClose();
  }

  async function handleConfirma({ id, codigo }) {
    switch (codigo) {
      case 4:
        return substituirPdf(id);
      case 1:
        return alterarStatus(
          id,
          'ativar',
          'Modelo reativado com sucesso.',
          'Erro ao reativar o modelo.',
        );
      case 2:
        return alterarStatus(
          id,
          'inativar',
          'Modelo inativado com sucesso.',
          'Erro ao inativar o modelo.',
          true,
        );
      case 3:
        return alterarStatus(
          id,
          'deletar',
          'Modelo excluído com sucesso.',
          'Erro ao excluir o modelo.',
        );
      default:
    }
  }

  function handleAtivar({ id, nome }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'REATIVAR MODELO DE DOCUMENTO',
        `Tem certeza que deseja reativar o modelo "${nome}"?`,
        { id, codigo: 1 },
      );
    }
  }

  function handleInativar({ id, nome, tem_pendentes: temPendentes }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'INATIVAR MODELO DE DOCUMENTO',
        temPendentes
          ? `O modelo "${nome}" possui documentos pendentes. Ao inativar, os documentos pendentes serão EXCLUÍDOS (documentos já concluídos são mantidos). Deseja continuar?`
          : `Tem certeza que deseja inativar o modelo "${nome}"?`,
        { id, codigo: 2 },
      );
    }
  }

  function handleDeletar({ id, nome }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'EXCLUIR MODELO DE DOCUMENTO',
        `Tem certeza que deseja excluir o modelo "${nome}"?`,
        { id, codigo: 3 },
      );
    }
  }

  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_MODELOS_DOCUMENTOS_LISTAR}>
      <>
        <ListPageTabbedComBuscaPagina
          title="Modelos de Documentos"
          itemLabelPlural="modelos"
          perPage={50}
          rowsPerPageOptions={[10, 25, 50, 100]}
          modoRolagemTabela="setas"
          colunasFixadasIniciais={{ nome: 'esquerda', acoes: 'direita' }}
          larguraFixadaEsquerda={300}
          larguraFixadaDireita={190}
          tabs={tabs.map((tab) => ({
            ...tab,
            contador: totais[tab.id] != null ? totais[tab.id] : undefined,
          }))}
          advancedFilters={advancedFilters}
          quickFilterSource="nome"
          quickFilterLabel="Nome"
          quickFilterPlaceholder="Buscar por nome..."
          onCreate={handleCriar}
          createLabel="Novo"
          listProps={{
            basePath: props.basePath,
            resource: props.resource,
            sort: { field: 'nome', order: 'ASC' },
            filterDefaultValues: { status: STATUS_ATIVO },
          }}
        >
          <Datagrid>
            <TextField source="nome" label="Nome" sortable={false} />
            <FunctionField
              label="Ação"
              render={(r) => <ChipAcao acao={r.acao} />}
              sortable={false}
            />
            <FunctionField
              label="Recorrência"
              render={(r) => textoRecorrencia[r.recorrencia] || '—'}
              sortable={false}
            />
            <FunctionField
              label="Quem assina"
              render={(r) => textoQuemAssina[r.quem_assina] || '—'}
              sortable={false}
            />
            <FunctionField
              label="Abrangência"
              render={(r) => textoAbrangencia[r.abrangencia] || '—'}
              sortable={false}
            />
            <ActionsField
              source="acoes"
              maxAcoesVisiveis={3}
              textAlign="right"
              label="Ações"
              sortable={false}
              actions={[
                {
                  title: 'Detalhes',
                  icon: <InfoOutlinedIcon />,
                  onClick: (r) => setModeloDetalhes(r),
                },
                {
                  title: (r) =>
                    r.tem_pendentes
                      ? 'Há documentos pendentes: clique para ver'
                      : 'Editar',
                  icon: <EditOutlinedIcon />,
                  onClick: (r) => handleEdit(r),
                },
                // Todas as ações aparecem em todas as linhas (as que não se aplicam ficam
                // desabilitadas), para a coluna não ficar desalinhada de uma linha à outra.
                {
                  title: 'Gerar documentos',
                  icon: <NoteAddOutlinedIcon />,
                  // Por colaborador é gerado na aba Documentos do detalhe; o resto, pelo cron (envio de
                  // arquivo: o RH só gera no sob demanda).
                  disabled: (r) =>
                    r.status !== STATUS_ATIVO ||
                    ehPorColaborador(r) ||
                    ehGeradoPeloCron(r) ||
                    (r.acao === 'enviar_arquivo' &&
                      r.recorrencia !== 'sob_demanda'),
                  onClick: (r) => setModeloGerar(r),
                },
                {
                  title: 'Substituir arquivo (inclui documentos pendentes)',
                  icon: <PublishOutlinedIcon />,
                  disabled: (r) =>
                    !(
                      r.status === STATUS_ATIVO &&
                      r.origem === 'upload' &&
                      Boolean(r.tem_pendentes)
                    ),
                  onClick: (r) => handleSubstituirPdf(r),
                },
                {
                  title: 'Ativar',
                  icon: <CheckCircleOutlineIcon />,
                  disabled: (r) => r.status !== STATUS_INATIVO,
                  onClick: (r) => handleAtivar(r),
                },
                {
                  title: 'Inativar',
                  icon: <ClearIcon />,
                  disabled: (r) => r.status !== STATUS_ATIVO,
                  onClick: (r) => handleInativar(r),
                },
                {
                  title: (r) =>
                    r.tem_pendentes
                      ? 'Não é possível excluir: há documentos pendentes'
                      : 'Excluir',
                  icon: <DeleteOutlineIcon />,
                  disabled: (r) =>
                    r.status !== STATUS_INATIVO || Boolean(r.tem_pendentes),
                  onClick: (r) => handleDeletar(r),
                },
              ]}
            />
          </Datagrid>
        </ListPageTabbedComBuscaPagina>

        <input
          ref={refInputPdf}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          hidden
          onChange={pdfEscolhido}
        />
        <DialogoConfirmacao
          ref={refDialogoConfirmacao}
          handleConfirma={handleConfirma}
        />
        <DialogoDetalhesModelo
          modelo={modeloDetalhes}
          onClose={() => setModeloDetalhes(null)}
        />
        <DialogoGerarDocumentos
          open={Boolean(modeloGerar)}
          modelo={modeloGerar}
          onClose={() => setModeloGerar(null)}
        />
        <DialogoCreateEdit
          open={dialogo.open}
          id={dialogo.id}
          onClose={() => {
            setDialogo({ open: false, id: null });
            handleClose();
          }}
          isDialogoConfirmacaoActions
        />
      </>
    </ProtegerPagina>
  );
};

export default List;
